import { useCallback, useEffect, useState } from 'react';
// @mui
import { Box, Typography } from '@mui/material';

// ----------------------------------------------------------------------

const ROCKET_SRC = '/assets/illustrations/illustration-rocket-small.webp';

function isStandaloneDisplay() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export default function NavSidebarPromo() {
  const [isStandalone, setIsStandalone] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);

  useEffect(() => {
    setIsStandalone(isStandaloneDisplay());

    const onPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const handleInstallPress = useCallback(async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    try {
      await installPrompt.userChoice;
    } catch {
      /* dismissed */
    }
    setInstallPrompt(null);
    setIsStandalone(isStandaloneDisplay());
  }, [installPrompt]);

  return (
    <Box sx={{ flexShrink: 0, px: 1.5, pb: 0.5, pt: 2.5 }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '20px',
          px: 2.5,
          py: 2.5,
          minHeight: 100,
          background: 'linear-gradient(135deg, #f8a170 0%, #6a3093 100%)',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.12)',
        }}
      >
        <Box
          aria-hidden
          sx={{
            pointerEvents: 'none',
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            borderRadius: '20px',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -24,
              left: -32,
              width: 128,
              height: 128,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.35)',
              filter: 'blur(24px)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: -24,
              top: '33%',
              width: 112,
              height: 112,
              borderRadius: '50%',
              bgcolor: 'rgba(251, 191, 36, 0.4)',
              filter: 'blur(24px)',
            }}
          />
        </Box>

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            minHeight: 100,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              component="p"
              sx={{
                m: 0,
                pr: 1,
                fontSize: 16,
                fontWeight: 500,
                lineHeight: 1.35,
                color: '#fff',
              }}
            >
              Power up your Productivity
            </Typography>

            {isStandalone ? (
              <Box
                component="span"
                sx={{
                  mt: 0.5,
                  width: 'fit-content',
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.25)',
                }}
              >
                App installed
              </Box>
            ) : (
              <Box
                component="button"
                type="button"
                onClick={handleInstallPress}
                sx={{
                  mt: 0.5,
                  width: 'fit-content',
                  border: 'none',
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#1c252e',
                  bgcolor: '#ffab00',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'background-color 0.2s, transform 0.15s',
                  '&:hover': { bgcolor: '#ffc233' },
                  '&:active': { transform: 'scale(0.98)' },
                }}
              >
                Install App
              </Box>
            )}
          </Box>

          <Box
            component="img"
            src={ROCKET_SRC}
            alt=""
            className="sidebar-rocket-float"
            sx={{
              position: 'absolute',
              right: -16,
              bottom: 0,
              width: 96,
              height: 128,
              objectFit: 'contain',
              objectPosition: 'bottom',
              pointerEvents: 'none',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
